import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Plus, Search, Trash2, X } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { createEmployee, deleteEmployee, getEmployees, updateEmployee } from '../ServiceAPI/AdminUser/api';
import '../styles/Admin.css';

const emptyForm = { adminId: '', name: '', email: '', password: '' };

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('adminAuth') !== 'true') {
      navigate('/admin/login');
      return;
    }
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    if (String(adminUser.Role || adminUser.role || '').toLowerCase() !== 'admin') {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await getEmployees();
      setEmployees(Array.isArray(response) ? response : response.employees || []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const openCreateModal = () => {
    setEditingEmployee(null);
    setFormData(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    setFormData({ adminId: employee.AdminId, name: employee.Name, email: employee.Email, password: '' });
    setError('');
    setShowModal(true);
  };

  const handleChange = ({ target }) => {
    setFormData((current) => ({ ...current, [target.name]: target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        adminId: Number(formData.adminId),
        name: formData.name,
        email: formData.email,
        ...(formData.password ? { password: formData.password } : {})
      };
      if (editingEmployee) {
        await updateEmployee(editingEmployee.Id, payload);
      } else {
        await createEmployee(payload);
      }
      setShowModal(false);
      await loadEmployees();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Delete employee ${employee.Name}? They will no longer be able to log in.`)) return;
    try {
      await deleteEmployee(employee.Id);
      await loadEmployees();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to delete employee');
    }
  };

  const filteredEmployees = employees.filter((employee) => {
    const search = searchTerm.toLowerCase();
    return !search || [employee.Name, employee.Email, String(employee.AdminId)].some((value) =>
      String(value || '').toLowerCase().includes(search)
    );
  });

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Employees</h1>
          <p className="admin-page-subtitle">Create and manage employee access</p>
        </div>
        <button className="btn-primary-modern" onClick={openCreateModal}>
          <Plus size={17} /> Add employee
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="admin-content-area">
      <div className="data-table-card">
        <div className="data-table-header">
          <h3>Active employees ({filteredEmployees.length})</h3>
        </div>
        <div className="search-filter-section">
          <div className="search-box">
            <Search size={20} />
            <input
              className="search-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by Admin ID, Name, Email..."
              aria-label="Search employees"
            />
          </div>
        </div>
        {loading ? (
          <div className="loading-container"><div className="spinner-large"></div><p>Loading employees...</p></div>
        ) : (
          <div className="table-responsive">
            <table className="modern-table">
              <thead><tr><th>Admin ID</th><th>Name</th><th>Email</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredEmployees.map((employee) => (
                  <tr key={employee.Id}>
                    <td><span className="td-badge">{employee.AdminId}</span></td>
                    <td>{employee.Name}</td>
                    <td>{employee.Email}</td>
                    <td><span className="status-badge-modern active">Active</span></td>
                    <td><div className="table-action-group">
                      <button className="action-btn primary" onClick={() => openEditModal(employee)} title="Edit employee" aria-label={`Edit ${employee.Name}`}><Edit2 size={16} /></button>
                      <button className="action-btn danger" onClick={() => handleDelete(employee)} title="Delete employee" aria-label={`Delete ${employee.Name}`}><Trash2 size={16} /></button>
                    </div></td>
                  </tr>
                ))}
                {!filteredEmployees.length && <tr><td colSpan="5">No employees found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {showModal && <div className="modal-overlay-modern" onMouseDown={(event) => event.target === event.currentTarget && setShowModal(false)}>
        <div className="modal-container-modern">
          <div className="modal-header-modern">
            <h3>{editingEmployee ? 'Edit employee' : 'Add employee'}</h3>
            <button className="modal-close-btn" onClick={() => setShowModal(false)} aria-label="Close"><X size={20} /></button>
          </div>
          <form className="modal-form-modern" onSubmit={handleSubmit}>
            <div className="form-group-modern"><label htmlFor="adminId">Admin ID *</label><input id="adminId" name="adminId" type="number" min="1" required value={formData.adminId} onChange={handleChange} /></div>
            <div className="form-row"><div className="form-group-modern"><label htmlFor="name">Full name *</label><input id="name" name="name" required value={formData.name} onChange={handleChange} /></div>
            <div className="form-group-modern"><label htmlFor="email">Email *</label><input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} /></div></div>
            <div className="form-group-modern"><label htmlFor="password">{editingEmployee ? 'New password (optional)' : 'Password *'}</label><input id="password" name="password" type="password" minLength="6" required={!editingEmployee} value={formData.password} onChange={handleChange} /></div>
            <div className="modal-footer-modern"><button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button><button className="btn-primary-modern" type="submit" disabled={saving}>{saving ? 'Saving...' : editingEmployee ? 'Save changes' : 'Create employee'}</button></div>
          </form>
        </div>
      </div>}
    </AdminLayout>
  );
};

export default EmployeeManagement;
